import { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
  Container,
  Avatar,
  Button,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MiscellaneousServicesOutlinedIcon from "@mui/icons-material/MiscellaneousServicesOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import LockResetIcon from "@mui/icons-material/LockReset";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

import { API_URL } from "../App";
import { fetchUserData } from "../components/auth";
import { UserDataContext } from "./UserDataContext";
import { apiRequest } from "../components/Config";

function VN() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, setUserData } = useContext(UserDataContext);
  const [expire, setExpire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const logout = async () => {
    try {
      await apiRequest("logout", "POST");
      setUserData({ userId: 0, user: "", fullName: "" });
      navigate("/login");
    } catch (error) {
      console.error("Błąd podczas wylogowywania:", error);
    }
  };

  useEffect(() => {
    const verifyUser = async () => {
      try {
        const userDataAnswer = await fetchUserData();

        if (userDataAnswer?.userId) {
          setUserData(userDataAnswer);
          setExpire(userDataAnswer.expire);

          if (
            userDataAnswer.veryf === 1 &&
            !location.pathname.startsWith("/portfolio/")
          ) {
            navigate(`/portfolio/${userDataAnswer.userId}`);
            return;
          }
        } else if (
          ![
            "/services",
            "/registernanny",
            "/registerclient",
            "/description",
            "/login",
            "/swiadczenie-wspierajace",
          ].some((path) => location.pathname === path)
        ) {
          navigate("/login");
        }
      } catch (error) {
        if (location.pathname !== "/services") navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    verifyUser();
  }, [navigate, setUserData, location.pathname]);

  useEffect(() => {
    if (expire) {
      const today = new Date();
      const input = new Date(expire);
      today.setHours(0, 0, 0, 0);
      input.setHours(0, 0, 0, 0);
      if (input < today) navigate("/expired");
    }
  }, [expire, navigate]);

  const pages = [
    {
      title: "Logowanie",
      path: "/",
      icon: <LoginIcon fontSize="small" />,
    },
    {
      title: "Opis aplikacji",
      path: "/description",
      icon: <InfoOutlinedIcon fontSize="small" />,
    },
    {
      title: "Usługi",
      path: "/services",
      icon: <MiscellaneousServicesOutlinedIcon fontSize="small" />,
    },

    ...(userData?.userId
      ? [
          {
            title: "Opiekunki",
            path: "/list",
            icon: <FavoriteBorderIcon fontSize="small" />,
          },
          {
            title: "Mój profil",
            path: "/profile",
            icon: <AccountCircleOutlinedIcon fontSize="small" />,
          },
        ]
      : []),

    ...(!userData?.userId
      ? [
          {
            title: "Dołącz jako opiekunka",
            path: "/registernanny",
            icon: <HowToRegOutlinedIcon fontSize="small" />,
          },
          {
            title: "Szukam opiekunki",
            path: "/registerclient",
            icon: <PersonAddOutlinedIcon fontSize="small" />,
          },
          {
            title: "Świadczenie wspierające",
            path: "/swiadczenie-wspierajace",
            icon: <VolunteerActivismOutlinedIcon fontSize="small" />,
          },
        ]
      : []),
  ];

  const userPhotoUrl = useMemo(() => {
    if (!userData?.userId) return null;
    return `${API_URL}/get-user-photo/${userData.userId}`;
  }, [userData?.userId]);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: alpha("#ffffff", 0.8),
        backdropFilter: "blur(16px)",
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        top: 0,
        zIndex: 1100,
        transition: "all 0.3s ease",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{ height: 70, justifyContent: "space-between" }}
        >
          {/* BRAND LOGO - Wersja Desktopowa i Mobilna */}
          <Typography
            variant="h6"
            onClick={() => navigate("/")}
            sx={{
              cursor: "pointer",
              fontWeight: 800,
              letterSpacing: "-0.5px",
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
              mr: 3,
            }}
          >
            TWOJA OPIEKUNKA
          </Typography>

          {/* MENU MOBILNE (HAMBURGER) */}
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              onClick={handleOpenNavMenu}
              sx={{ color: "text.primary" }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElNav}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              PaperProps={{
                elevation: 0,
                sx: {
                  borderRadius: "20px",
                  mt: 1.5,
                  minWidth: 240,
                  p: 1,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                },
              }}
            >
              {pages.map((page) => {
                const isActive = location.pathname === page.path;
                return (
                  <MenuItem
                    key={page.title}
                    onClick={() => {
                      navigate(page.path);
                      handleCloseNavMenu();
                    }}
                    selected={isActive}
                    sx={{
                      borderRadius: "12px",
                      py: 1.2,
                      px: 2,
                      my: 0.5,
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? "primary.main" : "text.primary",
                      "&.Mui-selected": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.08,
                        ),
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.12,
                          ),
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        mr: 2,
                        display: "flex",
                        color: isActive ? "primary.main" : "text.secondary",
                      }}
                    >
                      {page.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={isActive ? 600 : 500}
                    >
                      {page.title}
                    </Typography>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>

          {/* MENU DESKTOP */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {pages.map((page) => {
              const isActive = location.pathname === page.path;
              return (
                <Button
                  key={page.title}
                  onClick={() => navigate(page.path)}
                  startIcon={page.icon}
                  disableRipple
                  sx={{
                    color: isActive ? "primary.main" : "text.secondary",
                    fontWeight: isActive ? 600 : 500,
                    textTransform: "none",
                    borderRadius: "20px",
                    fontSize: "0.9rem",
                    px: 2,
                    py: 0.8,
                    transition: "all 0.2s ease-in-out",
                    backgroundColor: isActive
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "transparent",
                    "&:hover": {
                      backgroundColor: isActive
                        ? alpha(theme.palette.primary.main, 0.12)
                        : alpha(theme.palette.action.hover, 0.06),
                      color: "primary.main",
                    },
                  }}
                >
                  {page.title}
                </Button>
              );
            })}
          </Box>

          {/* SEKCJA PROFILU / LOGOWANIA */}
          <Box sx={{ flexGrow: 0, display: "flex", alignItems: "center" }}>
            {!loading && userData?.userId ? (
              <>
                <Box
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    flexDirection: "column",
                    alignItems: "flex-end",
                    mr: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      lineHeight: 1.2,
                    }}
                  >
                    {userData.fullName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    }}
                  >
                    ID: #{userData.userId}
                  </Typography>
                </Box>

                <Tooltip title="Ustawienia profilu">
                  <IconButton
                    onClick={handleOpenUserMenu}
                    sx={{
                      p: 0.5,
                      transition: "transform 0.2s ease",
                      "&:hover": { transform: "scale(1.05)" },
                    }}
                  >
                    <Avatar
                      src={userPhotoUrl}
                      alt={userData.fullName}
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "primary.main",
                        boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
                        fontSize: "0.95rem",
                        fontWeight: 700,
                      }}
                    >
                      {userData.fullName?.charAt(0)}
                    </Avatar>
                  </IconButton>
                </Tooltip>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    mx: 1.5,
                    height: 24,
                    my: "auto",
                    borderColor: alpha(theme.palette.divider, 0.1),
                    display: { xs: "none", sm: "block" },
                  }}
                />

                <Tooltip title="Wyloguj się">
                  <IconButton
                    onClick={logout}
                    sx={{
                      color: "text.secondary",
                      borderRadius: "12px",
                      p: 1,
                      "&:hover": {
                        color: "error.main",
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <LogoutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: 1.5 }}
                  anchorEl={anchorElUser}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                  disableScrollLock={true}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      borderRadius: "16px",
                      width: 220,
                      boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      p: 0.8,
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      navigate("/passchange");
                      handleCloseUserMenu();
                    }}
                    sx={{ borderRadius: "10px", py: 1 }}
                  >
                    <LockResetIcon
                      sx={{ mr: 1.5, fontSize: 20, color: "text.secondary" }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                      Zmiana hasła
                    </Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      navigate("/groups");
                      handleCloseUserMenu();
                    }}
                    sx={{ borderRadius: "10px", py: 1 }}
                  >
                    <GroupsOutlinedIcon
                      sx={{ mr: 1.5, fontSize: 20, color: "text.secondary" }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                      Zarządzaj grupą
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              location.pathname !== "/login" && (
                <Button
                  variant="contained"
                  onClick={() => navigate("/login")}
                  disableElevation
                  sx={{
                    borderRadius: "100px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    px: 3,
                    py: 0.9,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                    "&:hover": {
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                    },
                  }}
                >
                  Zaloguj się
                </Button>
              )
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default VN;
